import type { RouteRecord } from 'vite-react-ssg'
import RootLayout from './components/layout/RootLayout'
import { getAnnouncement } from './lib/wp'

// A deploy replaces every content-hashed chunk. Any tab that was already open
// (or holding a cached HTML shell) then 404s when it lazily imports a route,
// which surfaces to the user as React Router's "Unexpected Application Error!".
// Recover by reloading once to pick up the new shell; the sessionStorage guard
// stops a genuinely-broken chunk from becoming a reload loop.
const RELOAD_KEY = 'mh_chunk_reload'

function lazy(load: () => Promise<unknown>) {
  return async () => {
    try {
      const mod = await load()
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(RELOAD_KEY)
      return mod as never
    } catch (err) {
      if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1')
        window.location.reload()
        // Hang until the reload takes over, so the error boundary never renders.
        await new Promise<never>(() => {})
      }
      throw err
    }
  }
}

// Route-level code splitting: every page is a lazy chunk. vite-react-ssg awaits
// these during the static build so each route still ships fully pre-rendered HTML.
export const routes: RouteRecord[] = [
  {
    path: '/',
    id: 'root',
    loader: async () => ({ announcement: await getAnnouncement() }),
    element: <RootLayout />,
    children: [
      { index: true, lazy: lazy(() => import('./pages/Home')) },
      { path: 'learn', lazy: lazy(() => import('./pages/Learn')) },
      { path: 'articles', lazy: lazy(() => import('./pages/Articles')) },
      { path: 'articles/:slug', lazy: lazy(() => import('./pages/Article')) },
      { path: 'leaderboard', lazy: lazy(() => import('./pages/Leaderboard')) },
      { path: 'reviewers/:slug', lazy: lazy(() => import('./pages/Reviewer')) },
      { path: 'supplements', lazy: lazy(() => import('./pages/Supplements')) },
      { path: 'peptides', lazy: lazy(() => import('./pages/Peptides')) },
      { path: 'about', lazy: lazy(() => import('./pages/About')) },
      { path: 'contact', lazy: lazy(() => import('./pages/Contact')) },
      { path: 'medical-disclaimer', lazy: lazy(() => import('./pages/MedicalDisclaimer')) },
      { path: 'privacy', lazy: lazy(() => import('./pages/Privacy')) },
      { path: 'terms', lazy: lazy(() => import('./pages/Terms')) },
      { path: '404', lazy: lazy(() => import('./pages/NotFound')) },
      { path: '*', lazy: lazy(() => import('./pages/NotFound')) },
    ],
  },
]
